/**
 * ProtectedModuleRoute.tsx
 *
 * Route protection component that ensures modules are enabled before allowing access.
 * If a module is disabled, shows a full-page "Module Not Enabled" message with
 * navigation options.
 *
 * Usage:
 * <ProtectedModuleRoute moduleId="servers">
 *   <ServersListRoute />
 * </ProtectedModuleRoute>
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Puzzle, Home, AlertTriangle, Settings, RefreshCw } from 'lucide-react';
import { useModule, useIsModuleAccessible } from '@/base/contexts/ModuleContext';

// ============================================================================
// Types
// ============================================================================

export interface ProtectedModuleRouteProps {
  /** The module ID to check (e.g., "servers", "kubernetes") */
  moduleId: string;
  /** The content to render if the module is enabled */
  children: ReactNode;
}

// Module display names mapping
const MODULE_DISPLAY_NAMES: Record<string, string> = {
  'servers': 'Physical Servers',
  'virtual-servers': 'Virtual Servers',
  'kubernetes': 'Kubernetes',
  'containers': 'Containers',
  'nutanix': 'Nutanix',
  'proxmox': 'Proxmox',
  'vdi': 'VDI',
  'active-directory': 'Active Directory',
  'identity-providers': 'Identity Providers',
  'splunk': 'Splunk',
  'file-shares': 'File Shares',
};

// ============================================================================
// Component
// ============================================================================

export function ProtectedModuleRoute({ moduleId, children }: ProtectedModuleRouteProps) {
  const module = useModule(moduleId);
  const isAccessible = useIsModuleAccessible(moduleId);

  const moduleName = MODULE_DISPLAY_NAMES[moduleId] || module?.name || moduleId;

  // Module doesn't exist in manifest
  if (!module) {
    return renderModuleNotFound(moduleId, moduleName);
  }

  // Module is accessible - render children
  if (isAccessible) {
    // Show setup banner if enabled but not configured
    if (module.state === 'ENABLED' && module.configured === 'not_configured') {
      return (
        <>
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
            <div className="flex items-center gap-3 max-w-7xl mx-auto">
              <AlertTriangle size={20} className="text-yellow-600" />
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">{moduleName}</span> requires initial setup.{' '}
                <Link to="/config/modules" className="underline hover:text-yellow-900">
                  Configure module
                </Link>
              </p>
            </div>
          </div>
          {children}
        </>
      );
    }

    // Show warning banner if degraded or has errors
    if (module.healthStatus === 'degraded' || module.healthStatus === 'failed') {
      return (
        <>
          <div className="bg-orange-50 border-b border-orange-200 px-4 py-3">
            <div className="flex items-center gap-3 max-w-7xl mx-auto">
              <AlertTriangle size={20} className="text-orange-600" />
              <p className="text-sm text-orange-800">
                <span className="font-semibold">{moduleName}</span> is experiencing issues.
                Some features may be unavailable.
              </p>
            </div>
          </div>
          {children}
        </>
      );
    }

    // Show upgrading overlay
    if (module.state === 'UPGRADING') {
      return (
        <>
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
            <div className="flex items-center gap-3 max-w-7xl mx-auto">
              <RefreshCw size={20} className="text-blue-600 animate-spin" />
              <p className="text-sm text-blue-800">
                <span className="font-semibold">{moduleName}</span> is being upgraded.
                Please wait...
              </p>
            </div>
          </div>
          <div className="min-h-screen flex items-center justify-center bg-[var(--grok-surface-1)] opacity-60">
            <div className="text-center">
              <RefreshCw size={48} className="text-[var(--ems-primary)] animate-spin mx-auto mb-4" />
              <p className="text-lg text-[var(--grok-text-secondary)]">Upgrading module...</p>
            </div>
          </div>
        </>
      );
    }

    // Normal rendering
    return <>{children}</>;
  }

  // Module is not accessible - determine why and show appropriate UI
  if (module.state === 'DISABLED') {
    return renderModuleDisabled(moduleId, moduleName);
  }

  if (module.state === 'UNINSTALLED' || module.state === 'INSTALLED') {
    return renderModuleNotInstalled(moduleId, moduleName);
  }

  if (!module.entitled) {
    return renderModuleLocked(moduleId, moduleName);
  }

  if (module.state === 'ERROR') {
    return renderModuleError(moduleId, moduleName);
  }

  // Fallback - module is not accessible for unknown reason
  return renderModuleNotEnabled(moduleId, moduleName);
}

// ============================================================================
// Helper Render Functions
// ============================================================================

function renderModuleNotFound(moduleId: string, moduleName: string) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--grok-surface-1)]">
      <div className="max-w-2xl w-full">
        <div className="card">
          <div className="card-body text-center py-16 px-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[var(--grok-surface-2)] flex items-center justify-center">
                <Puzzle size={40} className="text-[var(--grok-text-muted)]" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[var(--grok-text-primary)] mb-3">
              Module Not Found
            </h1>
            <p className="text-lg text-[var(--grok-text-secondary)] mb-8 max-w-md mx-auto">
              The <span className="font-semibold text-[var(--grok-text-primary)]">{moduleName}</span> module
              is not available in this system.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--grok-surface-2)] text-[var(--grok-text-muted)] mb-8">
              <Puzzle size={16} />
              <span className="text-sm font-medium">Module: {moduleId}</span>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[var(--ems-primary)] text-white font-medium hover:bg-[var(--ems-primary-hover)]"
            >
              <Home size={20} />
              <span>Go to Dashboard</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderModuleDisabled(moduleId: string, moduleName: string) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--grok-surface-1)]">
      <div className="max-w-2xl w-full">
        <div className="card">
          <div className="card-body text-center py-16 px-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[var(--grok-surface-2)] flex items-center justify-center">
                <Lock size={40} className="text-[var(--grok-text-muted)]" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[var(--grok-text-primary)] mb-3">
              Module Disabled
            </h1>
            <p className="text-lg text-[var(--grok-text-secondary)] mb-8 max-w-md mx-auto">
              The <span className="font-semibold text-[var(--grok-text-primary)]">{moduleName}</span> module
              is currently disabled.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--grok-surface-2)] text-[var(--grok-text-muted)] mb-8">
              <Puzzle size={16} />
              <span className="text-sm font-medium">Module: {moduleId}</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/config/modules"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[var(--ems-primary)] text-white font-medium hover:bg-[var(--ems-primary-hover)] min-w-[200px] justify-center"
              >
                <Settings size={20} />
                <span>Module Settings</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[var(--grok-border)] bg-[var(--grok-surface-2)] text-[var(--grok-text-primary)] font-medium hover:bg-[var(--grok-hover-bg)] min-w-[200px] justify-center"
              >
                <Home size={20} />
                <span>Go to Dashboard</span>
              </Link>
            </div>
            <div className="mt-12 pt-8 border-t border-[var(--grok-border)]">
              <p className="text-sm text-[var(--grok-text-muted)]">
                An administrator can enable this module in Module Settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderModuleNotInstalled(moduleId: string, moduleName: string) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--grok-surface-1)]">
      <div className="max-w-2xl w-full">
        <div className="card">
          <div className="card-body text-center py-16 px-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[var(--grok-surface-2)] flex items-center justify-center">
                <Puzzle size={40} className="text-[var(--grok-text-muted)]" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[var(--grok-text-primary)] mb-3">
              Module Not Installed
            </h1>
            <p className="text-lg text-[var(--grok-text-secondary)] mb-8 max-w-md mx-auto">
              The <span className="font-semibold text-[var(--grok-text-primary)]">{moduleName}</span> module
              is not currently installed in this system.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--grok-surface-2)] text-[var(--grok-text-muted)] mb-8">
              <Puzzle size={16} />
              <span className="text-sm font-medium">Module: {moduleId}</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/config/modules"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[var(--ems-primary)] text-white font-medium hover:bg-[var(--ems-primary-hover)] min-w-[200px] justify-center"
              >
                <Settings size={20} />
                <span>Module Settings</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[var(--grok-border)] bg-[var(--grok-surface-2)] text-[var(--grok-text-primary)] font-medium hover:bg-[var(--grok-hover-bg)] min-w-[200px] justify-center"
              >
                <Home size={20} />
                <span>Go to Dashboard</span>
              </Link>
            </div>
            <div className="mt-12 pt-8 border-t border-[var(--grok-border)]">
              <p className="text-sm text-[var(--grok-text-muted)]">
                An administrator must install and configure this module before it can be used.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderModuleLocked(_moduleId: string, moduleName: string) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--grok-surface-1)]">
      <div className="max-w-2xl w-full">
        <div className="card">
          <div className="card-body text-center py-16 px-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock size={40} className="text-amber-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[var(--grok-text-primary)] mb-3">
              License Required
            </h1>
            <p className="text-lg text-[var(--grok-text-secondary)] mb-8 max-w-md mx-auto">
              The <span className="font-semibold text-[var(--grok-text-primary)]">{moduleName}</span> module
              requires a valid license to access.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 mb-8">
              <Lock size={16} />
              <span className="text-sm font-medium">Licensed Module</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[var(--ems-primary)] text-white font-medium hover:bg-[var(--ems-primary-hover)] min-w-[200px] justify-center"
              >
                <Home size={20} />
                <span>Go to Dashboard</span>
              </Link>
            </div>
            <div className="mt-12 pt-8 border-t border-[var(--grok-border)]">
              <p className="text-sm text-[var(--grok-text-muted)]">
                Contact your administrator to request access to this module.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderModuleError(_moduleId: string, moduleName: string) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--grok-surface-1)]">
      <div className="max-w-2xl w-full">
        <div className="card">
          <div className="card-body text-center py-16 px-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle size={40} className="text-red-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[var(--grok-text-primary)] mb-3">
              Module Error
            </h1>
            <p className="text-lg text-[var(--grok-text-secondary)] mb-8 max-w-md mx-auto">
              The <span className="font-semibold text-[var(--grok-text-primary)]">{moduleName}</span> module
              encountered an error and cannot be loaded.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 text-red-800 mb-8">
              <AlertTriangle size={16} />
              <span className="text-sm font-medium">Error State</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/config/modules"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[var(--ems-primary)] text-white font-medium hover:bg-[var(--ems-primary-hover)] min-w-[200px] justify-center"
              >
                <Settings size={20} />
                <span>Module Settings</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[var(--grok-border)] bg-[var(--grok-surface-2)] text-[var(--grok-text-primary)] font-medium hover:bg-[var(--grok-hover-bg)] min-w-[200px] justify-center"
              >
                <Home size={20} />
                <span>Go to Dashboard</span>
              </Link>
            </div>
            <div className="mt-12 pt-8 border-t border-[var(--grok-border)]">
              <p className="text-sm text-[var(--grok-text-muted)]">
                Check the system logs or contact your administrator for more information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderModuleNotEnabled(moduleId: string, moduleName: string) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--grok-surface-1)]">
      <div className="max-w-2xl w-full">
        <div className="card">
          <div className="card-body text-center py-16 px-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[var(--grok-surface-2)] flex items-center justify-center">
                <Lock size={40} className="text-[var(--grok-text-muted)]" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[var(--grok-text-primary)] mb-3">
              Module Not Enabled
            </h1>
            <p className="text-lg text-[var(--grok-text-secondary)] mb-8 max-w-md mx-auto">
              The <span className="font-semibold text-[var(--grok-text-primary)]">{moduleName}</span> module
              is not currently enabled in this system.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--grok-surface-2)] text-[var(--grok-text-muted)] mb-8">
              <Puzzle size={16} />
              <span className="text-sm font-medium">Module: {moduleId}</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/config/modules"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[var(--ems-primary)] text-white font-medium hover:bg-[var(--ems-primary-hover)] min-w-[200px] justify-center"
              >
                <Puzzle size={20} />
                <span>Module Settings</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[var(--grok-border)] bg-[var(--grok-surface-2)] text-[var(--grok-text-primary)] font-medium hover:bg-[var(--grok-hover-bg)] min-w-[200px] justify-center"
              >
                <Home size={20} />
                <span>Go to Dashboard</span>
              </Link>
            </div>
            <div className="mt-12 pt-8 border-t border-[var(--grok-border)]">
              <p className="text-sm text-[var(--grok-text-muted)]">
                To access this module, an administrator must enable it in the Module Settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProtectedModuleRoute;
